import {Card} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';
import {isArray, noop} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  type DmsDataGridProps,
  Section,
  SectionTab,
  useCustomerUrl,
  useIsLicenceEnabled,
  usePermissions,
} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {VEHICLES_GRID_CODE, VEHICLES_OWNERSHIP_GRID_CODE} from '../../vehiclesGridCodes';

type VehiclesProps = Pick<DmsDataGridProps, 'maximization'> & {
  soldVehiclesCount?: number;
  serviceVehiclesCount?: number;
};

export function Vehicles(props: VehiclesProps) {
  const navigate = useNavigate();
  const {customerId} = useCustomerUrl();

  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });
  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const hasSalesLicence = canReadBusinessCase;

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      customer: [customerId],
    }),
    [customerId]
  );

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    switch (actionKey) {
      case 'detail':
        navigate(
          composePath(vehiclesRoutes.detail, {params: {id}, isSearchParamsPreserved: false})
        );
        break;
    }
  };

  const ownershipQueryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {customerId}]),
    [customerId]
  );

  const ownershipActionCallback: ActionCallback = ({actionKey, rowId}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', () =>
        navigate(composePath(vehiclesRoutes.detail, {params: {id}, isSearchParamsPreserved: false}))
      )
      .otherwise(noop);
  };

  const tabs = buildArray<SectionTab>()
    .when(hasSalesLicence, {
      queryId: 'vehicle',
      content: (
        <Card title={i18n.t('entity.customer.labels.soldVehicles')}>
          {/*  Datagrid inside tabs need to be wrapped in Box with unique key, to force Datagrid rerender between tab switching  */}
          <Box height={100} key="customer-vehicles-list">
            <DmsDataGrid
              maximization={props.maximization}
              gridCode={VEHICLES_GRID_CODE}
              data-testid={testIds.customer.detail('interests')}
              actionCallback={actionCallback}
              queryModifier={queryModifier}
            />
          </Box>
        </Card>
      ),
      title: `${i18n.t('entity.customer.labels.soldVehicles')} (${props.soldVehiclesCount ?? 0})`,
    })
    .when(isWorkshopModuleEnabled, {
      queryId: 'ownership',
      content: (
        <Card title={i18n.t('entity.customer.labels.serviceVehicles')}>
          {/*  Datagrid inside tabs need to be wrapped in Box with unique key, to force Datagrid rerender between tab switching  */}
          <Box key="customer-vehicles-ownership">
            <DmsDataGrid
              maximization={props.maximization}
              autoHeight
              data-testid={testIds.customer.detail('ownership')}
              gridCode={VEHICLES_OWNERSHIP_GRID_CODE}
              actionCallback={ownershipActionCallback}
              queryModifier={ownershipQueryModifier}
            />
          </Box>
        </Card>
      ),
      title: `${i18n.t('entity.customer.labels.serviceVehicles')} (${props.serviceVehiclesCount ?? 0})`,
    });

  return <Section tabs={tabs} />;
}
